/*
  User Consent Management
*/

type UserConsent = {
  analyticsStorage: boolean
  adStorage: boolean
  adUserData: boolean
  adPersonalization: boolean
  functionalityStorage: boolean
  personalizationStorage: boolean
  securityStorage: boolean
}

const userConsent = {
  defaultConsent: {
    analyticsStorage: false,
    adStorage: false,
    adUserData: false,
    adPersonalization: false,
    functionalityStorage: true,
    personalizationStorage: false,
    securityStorage: false
  },

  /** Reads the persisted user consent preferences from the cookie. */
  getCookie(): UserConsent | undefined {
    const cookies = Object.fromEntries(document.cookie.split('; ').map(v=>v.split(/=(.*)/s).map(decodeURIComponent)))
    if (!cookies['user_consent']) return undefined
    return JSON.parse(cookies['user_consent'])
  },

  /** Stores user consent preferences for one year. */
  setCookie(value: UserConsent) {
    const encodedValue = encodeURIComponent(JSON.stringify(value))
    const expiry = new Date()
    expiry.setTime(expiry.getTime() + (365 * 24 * 3600 * 1000))
    document.cookie = `user_consent=${encodedValue}; expires=${expiry.toUTCString()}; path=/`
  },

  /** Pushes the current consent state to Google Tag Manager. */
  sendToGtm() {
    const consent = userConsent.getCookie()
    userConsent.gtag('consent', 'update', userConsent.gtmConsent(consent || userConsent.defaultConsent))
    userConsent.gtag('event', 'cookie_consent_update')
  },

  /** Initializes the default GTM consent state on each Turbo page load. */
  start() {
    document.addEventListener('turbo:load', () => {
      userConsent.gtag('consent', 'default', userConsent.gtmConsent(userConsent.defaultConsent))
      userConsent.sendToGtm()
    })
  },

  /** Converts app consent keys to GTM consent keys and values. */
  gtmConsent(consent: UserConsent) {
    return Object.fromEntries(
      Object.entries(consent).map(
        ([k, v]) => [k.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase(), v ? 'granted' : 'denied']
      )
    )
  },

  /** Forwards a gtag command to the global dataLayer when GTM is present. */
  gtag(..._args: unknown[]){ (globalThis as any).dataLayer?.push(arguments) }
}

export default userConsent
